/**
 * Pure, framework-free pricing/checkout wiring used by:
 *   - src/components/landing/TiersSection.tsx   (CTA hrefs)
 *   - src/pages/Checkout.tsx                    (URL → Stripe lookup keys)
 *   - src/pages/SubscriptionSuccess.tsx         (return URL contract)
 *
 * Extracted so it can be unit-tested end-to-end without pulling React /
 * Stripe / router into the test environment. Every change here MUST keep
 * the `src/lib/__tests__/checkoutPricing.test.ts` suite green.
 */

import type { TierEnum } from "@/constants/tiers";

export const TIER_KEYS = ["pre_sales", "system_tech", "touring"] as const;
export type TierKey = typeof TIER_KEYS[number];
export type Interval = "monthly" | "yearly";

// Base monthly/yearly prices ($10 / $100) — used as the first line item.
export const BASE_PRICE_IDS = new Set<string>([
  "pre_sales_monthly", "pre_sales_yearly",
  "system_tech_monthly", "system_tech_yearly",
  "touring_monthly", "touring_yearly",
]);

// Add-on prices ($5 / $50) — used as additional line items when stacking.
export const ADDON_PRICE_IDS = new Set<string>([
  "pre_sales_addon_monthly", "pre_sales_addon_yearly",
  "system_tech_addon_monthly", "system_tech_addon_yearly",
  "touring_addon_monthly", "touring_addon_yearly",
]);

export const SUCCESS_PATH = "/subscription/success";
export const CANCELLED_PATH = "/subscription/cancelled";

/**
 * The success return URL Stripe substitutes `{CHECKOUT_SESSION_ID}` into.
 * Must always resolve to SUCCESS_PATH and preserve the session id.
 */
export function buildReturnUrl(origin: string): string {
  return `${origin}${SUCCESS_PATH}?session_id={CHECKOUT_SESSION_ID}`;
}

export function tierKeyFromLookupKey(id: string): TierKey | null {
  const key = id.replace(/_addon_(monthly|yearly)$/, "").replace(/_(monthly|yearly)$/, "");
  return (TIER_KEYS as readonly string[]).includes(key) ? key as TierKey : null;
}

/**
 * The href a pricing CTA links to. Signed-out users are routed through
 * `/auth`; signed-in users go straight to the checkout deep link.
 *
 * The free tier used to point straight at /configurator/custom for everyone,
 * which dropped anonymous visitors on /auth at the end of the form. Signed-out
 * visitors now create the free account first.
 */
export function ctaHrefFor(tier: TierEnum, isLoggedIn: boolean): string {
  if (tier === "free") {
    return isLoggedIn ? "/configurator/custom" : "/auth?tab=signup";
  }
  if (isLoggedIn) return `/checkout/${tier}_monthly`;
  // Signed-out users always land on plain signup — we no longer preselect a
  // tier or deep-link into checkout. Users subscribe from inside the app
  // after creating their free account.
  return "/auth?tab=signup";
}

/**
 * Resolve a URL param (`:priceId`) into the ordered list of Stripe
 * `lookup_key` values that must be sent to `create-checkout`.
 *
 * Accepts:
 *   - single base price id           → "pre_sales_monthly"
 *   - comma/plus-separated tier keys → "pre_sales,touring" or "a+b:yearly"
 * Stacking rule: first tier is the base price, remaining tiers become
 * `_addon_` prices at the same interval.
 */
export function resolvePriceIds(param: string): string[] | null {
  if (BASE_PRICE_IDS.has(param)) return [param];

  const [tiersPart, intervalPart] = param.split(":");
  const interval: Interval = intervalPart === "yearly" ? "yearly" : "monthly";
  const tiers = tiersPart
    .split(/[,+]/)
    .map((t) => t.trim())
    .filter(Boolean);

  if (tiers.length === 0) return null;
  for (const t of tiers) {
    if (!(TIER_KEYS as readonly string[]).includes(t)) return null;
  }
  const unique = Array.from(new Set(tiers)) as TierKey[];
  const [base, ...addons] = unique;
  const ids = [
    `${base}_${interval}`,
    ...addons.map((t) => `${t}_addon_${interval}`),
  ];
  for (const id of ids) {
    if (!BASE_PRICE_IDS.has(id) && !ADDON_PRICE_IDS.has(id)) return null;
  }
  return ids;
}

/**
 * Build the `/checkout/:priceId` URL for a base tier + selected add-on
 * tiers at the given interval. Mirrors the on-page add-on toggle in
 * `src/pages/Checkout.tsx` — the two MUST stay in sync so the "Add more
 * access" checkboxes always land on a valid Stripe deep link.
 */
export function buildCheckoutPath(
  baseTier: TierKey,
  addons: TierKey[],
  interval: Interval,
): string {
  const unique = Array.from(new Set(addons.filter((t) => t !== baseTier)));
  if (unique.length === 0) return `/checkout/${baseTier}_${interval}`;
  const tiers = [baseTier, ...unique].join(",");
  return `/checkout/${tiers}:${interval}`;
}
